/**
 * Cards, grid or list — the same three buttons wherever a page offers a choice
 * of density.
 *
 * The plant page grew this first, inline in its toolbar. When the locations
 * page needed the same thing it was pulled out, so the two cannot drift: the
 * glyphs, the pressed state and the keyboard behaviour are decided once.
 *
 * The chosen view wears the theme's highlight colour (terracotta in Grow, rust
 * in Field notes; owner, 2026-10-17: soft menus, terracotta kept for the
 * counts and List/Grid), not the menus' soft accent.
 *
 * @module ViewSwitch
 */
import React from 'react';
import { LayoutGrid, Rows3, GalleryVerticalEnd } from 'lucide-react';
import { cn } from '@/lib/utils';

/** The three densities every list in the app can be read at. */
export type ViewName = 'cards' | 'grid' | 'list';

/** Each density with the icon that says what it looks like. */
const GLYPHS: Record<ViewName, React.ElementType> = {
  cards: GalleryVerticalEnd,
  grid: LayoutGrid,
  list: Rows3,
};

/** The density chosen, how to change it, and the words to read out. */
interface ViewSwitchProps {
  /** The view drawn now. */
  value: ViewName;
  /** Called with the view pressed. */
  onChange: (view: ViewName) => void;
  /** The accessible name of the group, e.g. "View". */
  label: string;
  /** The accessible name of each option, in the app's language. */
  names: Record<ViewName, string>;
}

/** Three pressed-or-not buttons, one per density, as a labelled group. */
export const ViewSwitch: React.FC<ViewSwitchProps> = ({ value, onChange, label, names }) => (
  <div className="flex items-center rounded-md border" role="group" aria-label={label}>
    {(Object.keys(GLYPHS) as ViewName[]).map((view) => {
      const Glyph = GLYPHS[view];
      const active = value === view;
      return (
        <button
          key={view}
          type="button"
          onClick={() => onChange(view)}
          aria-pressed={active}
          aria-label={names[view]}
          title={names[view]}
          className={cn(
            'flex h-9 w-9 items-center justify-center transition-colors first:rounded-l-md last:rounded-r-md',
            active ? 'bg-highlight text-highlight-foreground' : 'text-muted-foreground hover:text-foreground',
          )}
        >
          <Glyph className="h-4 w-4" aria-hidden="true" />
        </button>
      );
    })}
  </div>
);
